import type { UIMessagePart } from '../../meta/build-assistant-message-meta';

/** Pull the citation-injected body from the last `data-content-replace` part,
 *  if any. The backend streams the raw text (no `[N]` tokens), then emits this
 *  event with the final content that has citations spliced in. Preferring it
 *  is v1 parity (`useAskMooChat`'s `replacedContent ?? extractTextContent`);
 *  without it, v2 renders the citation-less stream and no chips appear. Last
 *  one wins, matching v1's overwrite-on-each-event behavior. */
function replacedContent(parts: UIMessagePart[]): string | undefined {
  let content: string | undefined;
  for (const p of parts) {
    if (p.type === 'data-content-replace') {
      const c = (p.data as { content?: unknown } | undefined)?.content;
      if (typeof c === 'string') content = c;
    }
  }
  return content;
}

/** Moo's emoji reaction to a user message, carried as a `data-reaction` part
 *  (appended live from the transient stream chunk, or rebuilt from history). */
export function extractReaction(
  parts: UIMessagePart[] | undefined
): string | undefined {
  const part = parts?.findLast((p) => p.type === 'data-reaction');
  const emoji = (part?.data as { emoji?: unknown } | undefined)?.emoji;
  return typeof emoji === 'string' ? emoji : undefined;
}

/** Part type carrying the names of the files sent with a user message. The
 *  server never sees it: the text and document contexts travel in the request
 *  body, so this exists only to show the files in the bubble. */
export const ATTACHMENTS_PART = 'data-attachments';

export function buildAttachmentsPart(fileNames: readonly string[]): {
  type: typeof ATTACHMENTS_PART;
  data: { files: string[] };
} {
  return { type: ATTACHMENTS_PART, data: { files: [...fileNames] } };
}

export function extractAttachments(
  parts: UIMessagePart[] | undefined
): string[] {
  const part = parts?.findLast((p) => p.type === ATTACHMENTS_PART);
  const files = (part?.data as { files?: unknown } | undefined)?.files;
  if (!Array.isArray(files)) return [];
  return files.filter((f): f is string => typeof f === 'string');
}

export function extractText(parts: UIMessagePart[] | undefined): string {
  if (!parts) return '';
  return (
    replacedContent(parts) ??
    parts
      .filter(
        (p): p is UIMessagePart & { text: string } =>
          p.type === 'text' && typeof p.text === 'string'
      )
      .map((p) => p.text)
      .join('')
  );
}
